import { BACKEND_URL } from '@/lib/config'
import { getAuthToken } from '@/lib/supabase'

interface DialogResponse {
  success?: boolean
  email?: string
  error?: string
}

type GoogleConnectorType =
  | 'google_calendar'
  | 'google_drive'
  | 'google_docs'
  | 'google_contacts'

const CONNECTOR_CONFIG: Record<
  GoogleConnectorType,
  {
    oauthEndpoint: string
    displayName: string
    localStorageKey: string
    messageType: string
  }
> = {
  google_calendar: {
    oauthEndpoint: '/api/google-calendar/oauth/url',
    displayName: 'Google Calendar',
    localStorageKey: 'google_calendar_oauth',
    messageType: 'google_connector_oauth_result',
  },
  google_drive: {
    oauthEndpoint: '/api/google-drive/oauth/url',
    displayName: 'Google Drive',
    localStorageKey: 'google_drive_oauth',
    messageType: 'google_connector_oauth_result',
  },
  google_docs: {
    oauthEndpoint: '/api/google-docs/oauth/url',
    displayName: 'Google Docs',
    localStorageKey: 'google_docs_oauth',
    messageType: 'google_connector_oauth_result',
  },
  google_contacts: {
    oauthEndpoint: '/api/google-contacts/oauth/url',
    displayName: 'Google Contacts',
    localStorageKey: 'google_contacts_oauth',
    messageType: 'google_connector_oauth_result',
  },
}

function isOfficeEnvironment(): boolean {
  return (
    typeof Office !== 'undefined' &&
    Office.context?.ui?.displayDialogAsync !== undefined
  )
}

async function getGoogleOAuthUrl(
  connectorType: GoogleConnectorType,
  permission: 'read' | 'read_write' = 'read'
): Promise<string> {
  const token = await getAuthToken()
  if (!token) {
    throw new Error('Not authenticated')
  }

  if (!BACKEND_URL) {
    throw new Error('Backend URL not configured')
  }

  const config = CONNECTOR_CONFIG[connectorType]

  const response = await fetch(`${BACKEND_URL}${config.oauthEndpoint}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': 'true',
    },
    body: JSON.stringify({ permission }),
  })

  if (!response.ok) {
    const errorText = await response.text()
    let errorMessage = `Failed to get ${config.displayName} OAuth URL`
    try {
      const errorJson = JSON.parse(errorText)
      errorMessage = errorJson.detail || errorMessage
    } catch {
      // Use default error message
    }
    throw new Error(errorMessage)
  }

  const data = await response.json()
  return data.url
}

async function openGoogleConnectorAuthDialogOffice(
  connectorType: GoogleConnectorType,
  permission: 'read' | 'read_write' = 'read'
): Promise<{ success: boolean; email?: string } | null> {
  const googleOAuthUrl = await getGoogleOAuthUrl(connectorType, permission)
  const config = CONNECTOR_CONFIG[connectorType]

  return new Promise((resolve, reject) => {
    let resolved = false
    let dialog: Office.DialogHandler

    const handler = (event: Office.DialogParentMessageReceivedEventArgs) => {
      if (!resolved) {
        resolved = true
        try {
          const message = JSON.parse(event.message) as DialogResponse

          if (message.error) {
            console.error(
              `${config.displayName} auth error from dialog:`,
              message.error
            )
            dialog.close()
            setTimeout(() => reject(new Error(message.error)), 100)
          } else if (message.success && message.email) {
            dialog.close()
            setTimeout(
              () => resolve({ success: true, email: message.email }),
              100
            )
          } else {
            dialog.close()
            setTimeout(
              () =>
                reject(
                  new Error(`Invalid response from ${config.displayName} auth`)
                ),
              100
            )
          }
        } catch (error) {
          console.error(
            `Failed to parse ${config.displayName} auth response:`,
            error
          )
          dialog.close()
          setTimeout(
            () =>
              reject(
                new Error(`Failed to parse ${config.displayName} auth response`)
              ),
            100
          )
        }
      }
    }

    console.log(
      `Opening ${config.displayName} auth dialog directly to Google:`,
      googleOAuthUrl
    )

    Office.context.ui.displayDialogAsync(
      googleOAuthUrl,
      { height: 70, width: 40 },
      (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          dialog = result.value
          dialog.addEventHandler(
            Office.EventType.DialogMessageReceived,
            handler
          )

          dialog.addEventHandler(
            Office.EventType.DialogEventReceived,
            (event) => {
              const errorOrMessage = event.error

              if (errorOrMessage === 12002) {
                if (!resolved) {
                  resolved = true
                  reject(
                    new Error(
                      `${config.displayName} auth dialog closed by user`
                    )
                  )
                }
              } else if (errorOrMessage === 12006) {
                if (!resolved) {
                  resolved = true
                  reject(
                    new Error(
                      `${config.displayName} auth dialog navigation failed`
                    )
                  )
                }
              }
            }
          )
        } else {
          reject(
            new Error(
              `Failed to open ${config.displayName} auth dialog: ${result.error?.message || result.status}`
            )
          )
        }
      }
    )
  })
}

async function openGoogleConnectorAuthDialogPopup(
  connectorType: GoogleConnectorType,
  permission: 'read' | 'read_write' = 'read'
): Promise<{ success: boolean; email?: string } | null> {
  const googleOAuthUrl = await getGoogleOAuthUrl(connectorType, permission)
  const config = CONNECTOR_CONFIG[connectorType]

  const resultKey = `${config.localStorageKey}_result`
  const timestampKey = `${config.localStorageKey}_timestamp`

  return new Promise((resolve, reject) => {
    localStorage.removeItem(resultKey)
    localStorage.removeItem(timestampKey)

    console.log(
      `Opening ${config.displayName} auth popup directly to Google:`,
      googleOAuthUrl
    )

    const width = 500
    const height = 600
    const left = window.screenX + (window.outerWidth - width) / 2
    const top = window.screenY + (window.outerHeight - height) / 2
    const popup = window.open(
      googleOAuthUrl,
      `${connectorType}_oauth`,
      `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`
    )

    if (!popup) {
      reject(new Error('Failed to open popup window. Please allow popups.'))
      return
    }

    let resolved = false

    const messageHandler = (event: MessageEvent) => {
      if (
        event.data?.type === config.messageType &&
        event.data?.connector_type === connectorType &&
        !resolved
      ) {
        resolved = true
        window.removeEventListener('message', messageHandler)
        clearInterval(pollInterval)

        if (event.data.error) {
          reject(new Error(event.data.error))
        } else if (event.data.success && event.data.email) {
          resolve({ success: true, email: event.data.email })
        } else {
          reject(new Error('Invalid OAuth response'))
        }

        try {
          popup.close()
        } catch {
          // Ignore close errors
        }
      }
    }
    window.addEventListener('message', messageHandler)

    const pollInterval = setInterval(() => {
      if (resolved) {
        clearInterval(pollInterval)
        return
      }

      if (popup.closed) {
        clearInterval(pollInterval)
        window.removeEventListener('message', messageHandler)

        const result = localStorage.getItem(resultKey)
        if (result && !resolved) {
          resolved = true
          try {
            const data = JSON.parse(result) as DialogResponse
            localStorage.removeItem(resultKey)
            localStorage.removeItem(timestampKey)

            if (data.error) {
              reject(new Error(data.error))
            } else if (data.success && data.email) {
              resolve({ success: true, email: data.email })
            } else {
              reject(new Error('Invalid OAuth response'))
            }
          } catch {
            reject(new Error('Failed to parse OAuth response'))
          }
        } else if (!resolved) {
          resolved = true
          reject(new Error(`${config.displayName} auth cancelled`))
        }
        return
      }

      const result = localStorage.getItem(resultKey)
      const timestamp = localStorage.getItem(timestampKey)
      if (result && timestamp) {
        const age = Date.now() - parseInt(timestamp, 10)
        if (age < 30000) {
          resolved = true
          clearInterval(pollInterval)
          window.removeEventListener('message', messageHandler)

          try {
            const data = JSON.parse(result) as DialogResponse
            localStorage.removeItem(resultKey)
            localStorage.removeItem(timestampKey)

            if (data.error) {
              reject(new Error(data.error))
            } else if (data.success && data.email) {
              resolve({ success: true, email: data.email })
            } else {
              reject(new Error('Invalid OAuth response'))
            }

            try {
              popup.close()
            } catch {
              // Ignore close errors
            }
          } catch {
            reject(new Error('Failed to parse OAuth response'))
          }
        }
      }
    }, 500)

    setTimeout(
      () => {
        if (!resolved) {
          resolved = true
          clearInterval(pollInterval)
          window.removeEventListener('message', messageHandler)
          try {
            popup.close()
          } catch {
            // Ignore close errors
          }
          reject(new Error(`${config.displayName} auth timeout`))
        }
      },
      5 * 60 * 1000
    )
  })
}

export async function openGoogleConnectorAuthDialog(
  connectorType: GoogleConnectorType,
  permission: 'read' | 'read_write' = 'read'
): Promise<{ success: boolean; email?: string } | null> {
  if (!BACKEND_URL) {
    throw new Error('Backend URL not configured')
  }

  if (isOfficeEnvironment()) {
    console.log(`Using Office Dialog API for ${connectorType} auth`)
    return openGoogleConnectorAuthDialogOffice(connectorType, permission)
  }
  console.log(`Using popup window for ${connectorType} auth`)
  return openGoogleConnectorAuthDialogPopup(connectorType, permission)
}
